import { differenceInSeconds } from 'date-fns';
import { PickerValue } from 'react-mobile-picker';

export interface CountDownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const ONE_DAY = 60 * 60 * 24;
const ONE_HOUR = 60 * 60;
const ONE_MINUTE = 60;

const indonesianMonth = [
  'Januari',
  'Februari',
  'Maret',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Agustus',
  'September',
  'Oktober',
  'November',
  'Desember',
];

const generateTimeZoneName = (date: Date) => {
  const timeZoneName = date
    .toLocaleDateString('id', {
      day: '2-digit',
      timeZoneName: 'short',
      timeZone: 'Asia/Jakarta',
    })
    .slice(3)
    .toUpperCase();

  if (timeZoneName.match(/^(WIB|WITA|WIT)$/)) return timeZoneName;

  return 'WIB';
};

/**
 * @param {string|undefined} strDate date-times string format
 * @returns {number} timestamp in seconds
 */
export const toTimestamp = (strDate?: string) => {
  const date = strDate ? new Date(strDate) : new Date();
  return date.getTime() / 1000;
};

export const formatFilterDate = (date: Date | null) => {
  if (!date) return '';

  return new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000)
    .toISOString()
    .split('T')[0];
};

export const indonesianDate = (timestamp: any) => {
  const date = new Date(timestamp * 1000);

  return `${date.getDate()} ${
    indonesianMonth[date.getMonth()]
  } ${date.getFullYear()}`;
};

export const indonesianDateTime = (
  timestamp: any,
  withTimeZoneName = false
) => {
  const date = new Date(timestamp * 1000);

  return `${date.getDate()} ${
    indonesianMonth[date.getMonth()]
  } ${date.getFullYear()}, ${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes()
  ).padStart(2, '0')} ${withTimeZoneName ? generateTimeZoneName(date) : ''}`;
};

export const indonesianTime = (timestamp: any, withTimeZoneName = false) => {
  const date = new Date(timestamp * 1000);

  return `${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes()
  ).padStart(2, '0')} ${withTimeZoneName ? generateTimeZoneName(date) : ''}`;
};

// Function to determine the number of days in a month
export const daysInMonth = (month: number, year: number) => {
  // Day 0 is the last day in the previous month.
  // Because the month constructor is 0-based, this works nicely.
  // A bit of a hack, but that's basically what you're doing by subtracting 32.
  return new Date(year, month, 0).getDate();
};

export const generateCountDownTime = (expiredTime: number): CountDownTime => {
  const expiredDateTime = new Date(expiredTime * 1000);
  const now = new Date();

  const diffInSeconds = differenceInSeconds(expiredDateTime, now);

  if (diffInSeconds < 1) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    };
  }

  const days = Math.floor(diffInSeconds / ONE_DAY);
  const hours = Math.floor((diffInSeconds - days * ONE_DAY) / ONE_HOUR);
  const minutes = Math.floor(
    (diffInSeconds - days * ONE_DAY - hours * ONE_HOUR) / ONE_MINUTE
  );
  const seconds =
    diffInSeconds - days * ONE_DAY - hours * ONE_HOUR - minutes * ONE_MINUTE;
  return {
    days,
    hours,
    minutes,
    seconds,
  };
};

export const getDayArray = (year: number, month: number) => {
  const dayCount = new Date(year, month, 0).getDate();
  return Array.from({ length: dayCount }, (_, i) => String(i + 1).padStart(2, '0'));
}

export const formatDate = (date: PickerValue): string => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'
  ]
  
  const day = parseInt(date.day as string, 10)
  const month = parseInt(date.month as string, 10) - 1  // Adjust month to 0-indexed
  const year = date.year

  return `${day} ${months[month]} ${year}`
}

export const parseDate = (dateString: string): PickerValue => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'
  ]
  
  const [day, monthName, year] = dateString.split(' ')
  const month = months.indexOf(monthName) + 1
  
  return {
    year,
    month: month.toString().padStart(2, '0'),
    day: day.padStart(2, '0')
  }
}
